import React from "react";

// Mock Card components — replace with your actual UI components
const Card = ({ children, className }) => (
  <div className={`border rounded-lg shadow-sm p-4 ${className}`}>{children}</div>
);
const CardHeader = ({ children, className }) => <div className={`mb-2 ${className}`}>{children}</div>;
const CardTitle = ({ children, className }) => <h3 className={`${className}`}>{children}</h3>;
const CardContent = ({ children, className }) => <div className={`${className}`}>{children}</div>;

export default function IkigaiVennChart({ questions, answers }) {
  const collectAnswers = (category) =>
    questions
      .filter((q) => q.category === category)
      .map((q) => answers[q.id])
      .filter(Boolean);

  const love = collectAnswers("love");
  const goodAt = collectAnswers("goodAt");
  const worldNeeds = collectAnswers("worldNeeds");
  const paidFor = collectAnswers("paidFor");

  const hasAllCategories = love.length && goodAt.length && worldNeeds.length && paidFor.length;

  const truncateText = (text, maxLength = 15) =>
    text.length > maxLength ? text.substring(0, maxLength) + "..." : text;

  const passion = [...love, ...goodAt];
  const mission = [...love, ...worldNeeds];
  const profession = [...goodAt, ...paidFor];
  const vocation = [...worldNeeds, ...paidFor];
  const ikigai = [...love, ...goodAt, ...worldNeeds, ...paidFor];

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-3xl font-bold text-gray-800 mb-2">IKIGAI Venn Diagram</h2>
        <p className="text-gray-600">
          Visual representation of your life's purpose with your values
        </p>
      </div>

      <div className="flex justify-center items-center min-h-screen  p-8">
        <div className="relative w-full max-w-3xl aspect-square">
          <svg width="700" height="700" viewBox="0 0 700 700" className="drop-shadow-2xl">
            <defs>
              <filter id="shadow" x="-50%" y="-50%" width="200%" height="200%">
                <feDropShadow dx="0" dy="8" stdDeviation="15" floodColor="rgba(0,0,0,0.25)" />
              </filter>
            </defs>

            <circle
              cx="350"
              cy="210"
              r="180"
              fill="#D8B4FE"
              fillOpacity="0.7"
              stroke="white"
              strokeWidth="8"
              filter="url(#shadow)"
            />

            <circle
              cx="490"
              cy="350"
              r="180"
              fill="#BBF7D0"
              fillOpacity="0.7"
              stroke="white"
              strokeWidth="8"
              filter="url(#shadow)"
            />

            <circle
              cx="350"
              cy="490"
              r="180"
              fill="#BFDBFE"
              fillOpacity="0.7"
              stroke="white"
              strokeWidth="8"
              filter="url(#shadow)"
            />

            {/* Left circle - WHAT WORLD NEEDS (Light Yellow) */}
            <circle
              cx="210"
              cy="350"
              r="180"
              fill="#FEF3C7"
              fillOpacity="0.7"
              stroke="white"
              strokeWidth="8"
              filter="url(#shadow)"
            />

            {/* Main category labels - positioned outside circles */}
            <text x="350" y="110" textAnchor="middle" className="fill-black font-bold" style={{ fontSize: "14px" }}>
              WHAT YOU LOVE
            </text>

            <text x="578" y="300" textAnchor="middle" className="fill-black font-bold" style={{ fontSize: "14px" }}>
              WHAT YOU'RE
            </text>
            <text x="578" y="320" textAnchor="middle" className="fill-black font-bold" style={{ fontSize: "14px" }}>
              GOOD AT
            </text>

            <text x="102" y="320" textAnchor="middle" className="fill-black font-bold" style={{ fontSize: "14px" }}>
              WHAT WORLD
            </text>
            <text x="102" y="335" textAnchor="middle" className="fill-black font-bold" style={{ fontSize: "14px" }}>
              NEEDS
            </text>

            <text x="350" y="561" textAnchor="middle" className="fill-black font-bold" style={{ fontSize: "14px" }}>
              WHAT YOU CAN BE PAID FOR
            </text>

            {/* Items within each main circle area - "testing" text like in reference */}

            {/* WHAT YOU LOVE items (top circle) */}
            {Array.from({ length: 5 }, (_, i) => (
              <text key={i} x="350" y={140 + i * 18} textAnchor="middle" style={{ fontSize: "13px", fill: "#6B46C1" }}>
                testing
              </text>
            ))}

            {/* WHAT YOU'RE GOOD AT items (right circle) */}
            {Array.from({ length: 5 }, (_, i) => (
              <text key={i} x="571" y={340 + i * 18} textAnchor="middle" style={{ fontSize: "13px", fill: "#38A169" }}>
                testing
              </text>
            ))}

            {/* WHAT WORLD NEEDS items (left circle) */}
            {Array.from({ length: 5 }, (_, i) => (
              <text key={i} x="105" y={350 + i * 18} textAnchor="middle" style={{ fontSize: "13px", fill: "#B8860B" }}>
                testing
              </text>
            ))}

            {/* WHAT YOU CAN BE PAID FOR items (bottom circle) */}
            {Array.from({ length: 5 }, (_, i) => (
              <text key={i} x="350" y={575 + i * 18} textAnchor="middle" style={{ fontSize: "13px", fill: "#2B6CB0" }}>
                testing
              </text>
            ))}

            {/* Intersection labels positioned in overlapping areas */}

            {/* PASSION - top left intersection (Love + World Needs) */}
            <text x="254" y="251" textAnchor="middle" className="fill-black font-bold" style={{ fontSize: "16px" }}>
              PASSION
            </text>
            {Array.from({ length: 2 }, (_, i) => (
              <text key={i} x="254" y={271 + i * 15} textAnchor="middle" style={{ fontSize: "12px", fill: "#6B46C1" }}>
                testing
              </text>
            ))}

            {/* PROFESSION - top right intersection (Love + Good At) */}
            <text x="436" y="251" textAnchor="middle" className="fill-black font-bold" style={{ fontSize: "16px" }}>
              PROFESSION
            </text>
            {Array.from({ length: 2 }, (_, i) => (
              <text key={i} x="436" y={271 + i * 15} textAnchor="middle" style={{ fontSize: "12px", fill: "#38A169" }}>
                testing
              </text>
            ))}

            {/* VOCATION - bottom left intersection (World Needs + Paid For) */}
            <text x="248" y="426" textAnchor="middle" className="fill-black font-bold" style={{ fontSize: "16px" }}>
              VOCATION
            </text>
            {Array.from({ length: 2 }, (_, i) => (
              <text key={i} x="248" y={445 + i * 15} textAnchor="middle" style={{ fontSize: "12px", fill: "#B8860B" }}>
                testing
              </text>
            ))}

            {/* MISSION - bottom right intersection (Good At + Paid For) */}
            <text x="435" y="420" textAnchor="middle" className="fill-black font-bold" style={{ fontSize: "16px" }}>
              MISSION
            </text>
            {Array.from({ length: 2 }, (_, i) => (
              <text key={i} x="435" y={435 + i * 15} textAnchor="middle" style={{ fontSize: "12px", fill: "#E53E3E" }}>
                testing
              </text>
            ))}

            {/* Center IKIGAI - where all four circles overlap */}
            <text x="350" y="340" textAnchor="middle" className="fill-black font-bold" style={{ fontSize: "18px" }}>
              IKIGAI
            </text>
            {Array.from({ length: 2 }, (_, i) => (
              <text key={i} x="350" y={360 + i * 15} textAnchor="middle" style={{ fontSize: "12px", fill: "#D97706" }}>
                testing
              </text>
            ))}
          </svg>
        </div>
      </div>

      <div className="p-5">

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
          {[
            {
              label: "What You LOVE",
              items: love,
              color: "#CE93D8",
              lightColor: "#F3E5F5"
            },
            {
              label: "What You're GOOD AT",
              items: goodAt,
              color: "#FFB8B3",
              lightColor: "#FFE7E4"
            },
            {
              label: "What World Needs",
              items: worldNeeds,
              color: "#FFCC80",
              lightColor: "#FFF3E0"
            },
            {
              label: "What You Can Be Paid For",
              items: paidFor,
              color: "#8AA9FF",
              lightColor: "#E3F2FD"
            }
          ].map(({ label, items, color, lightColor }, i) => (
            <div
              key={i}
              className="rounded-xl p-4 shadow-sm bg-white"
              style={{
                border: `2px solid ${color}`,
              }}
            >
              <div className="flex items-center gap-2 mb-3">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: color }}></div>
                <h2 className="text-sm font-semibold" style={{ color }}>{label}</h2>
              </div>

              <div className="space-y-1">
                {items.length ? (
                  items.map((item, idx) => (
                    <div
                      key={idx}
                      className="text-sm px-3 py-2 rounded"
                      style={{
                        backgroundColor: idx % 1 === 0 ? lightColor : "transparent",
                      }}
                    >
                      {item}
                    </div>
                  ))
                ) : (
                  <p className="text-xs text-gray-500">Complete more questions to see values</p>
                )}
              </div>
            </div>
          ))}
        </div>



        {hasAllCategories && (
          <Card
            className="mt-8 rounded-xl shadow-md bg-[#f991b05e]"
            
          >  
            <CardContent className="flex items-start gap-4 p-6">
              {/* Icon Box */}
              <div className="bg-pink-500 text-white w-12 h-12 flex items-center justify-center rounded-md text-xl shadow-sm">
                🌸
              </div>

              {/* Content */}
              <div>
                <h3 className="text-lg font-semibold text-gray-800">Your IKIGAI Found!</h3>
                <p className="text-gray-700 mb-4 text-sm">
                  The center intersection represents your life’s purpose - where all four elements come together.
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                  <div>
                    <strong className="text-pink-600">Passion:</strong> {passion.slice(0, 2).join(", ")}
                  </div>
                  <div>
                    <strong className="text-purple-600">Profession:</strong> {profession.slice(0, 2).join(", ")}
                  </div>
                  <div>
                    <strong className="text-blue-600">Mission:</strong> {mission.slice(0, 2).join(", ")}
                  </div>
                  <div>
                    <strong className="text-orange-500">Vocation:</strong> {vocation.slice(0, 2).join(", ")}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
